import moment from "moment";

import type {
  ApprovalGroup,
  ApprovalStep,
  GuardrailDecisionLogEntry,
  GuardrailEvaluation,
} from "src/components/RequisitionGuardrailOutcome";

import { failingCheckLabels } from "./failingCheckLabels";

export const formatWorkspaceStamp = (
  at: string | null | undefined,
  kind: "date" | "datetime",
  formats: { readable: string; readableDateTime: string }
): string | null => {
  const value = at?.trim() ?? "";
  if (value === "") {
    return null;
  }
  const parsed =
    kind === "date" ? moment(value, "YYYY-MM-DD", true) : moment(value);
  if (!parsed.isValid()) {
    return null;
  }
  if (kind === "date") {
    return parsed.format(formats.readable);
  }
  return parsed.local().format(formats.readableDateTime);
};

export type HistoryIcon =
  | "file"
  | "clipboard-check"
  | "paper-plane"
  | "comment"
  | "check"
  | "xmark"
  | "clock";

export type HistoryRow = {
  id: string;
  icon: HistoryIcon;
  title: string;
  at: string | null;
  atKind: "date" | "datetime";
  datePrefix?: string;
  actorPersonId?: string | null;
  actorPersonName?: string;
  rescindedBy?: {
    personId?: string | null;
    personName?: string | null;
    actedAt?: string | null;
  } | null;
};

export type HistoryRequisition = {
  openedDate?: string | null;
  guardrailEvaluation?: GuardrailEvaluation | null;
  guardrailDecisionLog?: GuardrailDecisionLogEntry[] | null;
};

const text = (value: string | null | undefined): string => value?.trim() ?? "";

const ordered = (steps: ApprovalStep[] | null | undefined): ApprovalStep[] =>
  [...(steps ?? [])].sort((left, right) => left.order - right.order);

const groupName = (group: ApprovalGroup): string =>
  text(group.approverGroupName) || "Approver group";

const logGroupName = (entry: GuardrailDecisionLogEntry): string =>
  text(entry.approverGroupName) || "Approver group";

const personName = (group: ApprovalGroup): string =>
  text(group.personName) || "Unknown";

const logPersonName = (entry: GuardrailDecisionLogEntry): string =>
  text(entry.personName) || "Unknown";

const addedBy = (group: ApprovalGroup): string => {
  const names = (group.addingRules ?? [])
    .map((rule) => text(rule.ruleName) || text(rule.ruleId))
    .filter((name) => name !== "");
  if (names.length === 0) {
    return "";
  }
  return ` (added by rule: ${names.join(", ")})`;
};

const passedCount = (evaluation: GuardrailEvaluation): number =>
  evaluation.checks.filter((check) => check.state === "PASS").length;

const warningCount = (evaluation: GuardrailEvaluation): number =>
  evaluation.checks.filter((check) => {
    if (check.state === "PASS") {
      return false;
    }
    return (check.effect ?? "BLOCK") === "WARN";
  }).length;

const evaluatedTitle = (evaluation: GuardrailEvaluation): string => {
  const passed = passedCount(evaluation);
  const warnings = warningCount(evaluation);
  const warningWord = warnings === 1 ? "warning" : "warnings";
  return `Guardrails evaluated — ${passed} passed, ${warnings} ${warningWord}`;
};

const firstGroupNames = (steps: ApprovalStep[]): string => {
  const step = steps.find((item) => item.kind === "group");
  if (!step) {
    return "";
  }
  return (step.groups ?? []).map(groupName).join(", ");
};

const routedTitle = (
  evaluation: GuardrailEvaluation,
  steps: ApprovalStep[]
): string | null => {
  switch (evaluation.template) {
    case "exception": {
      const names = failingCheckLabels(evaluation.checks).join(", ");
      return names
        ? `Routed for approval — exceptions: ${names}`
        : "Routed for approval — exceptions";
    }
    case "fast-track":
      return "Fast-track route";
    case "standard": {
      const names = firstGroupNames(steps);
      return names ? `Routed for approval — ${names}` : "Routed for approval";
    }
    default:
      return null;
  }
};

const actedTitle = (group: ApprovalGroup): string | null => {
  const extra = addedBy(group);
  switch (group.status) {
    case "APPROVED":
      return `Approved by ${personName(group)} as ${groupName(group)}${extra}`;
    case "REFUSED": {
      const reasons = (group.reasons ?? [])
        .map((reason) => text(reason.label))
        .filter((label) => label !== "")
        .join(", ");
      const comment = text(group.comment);
      let title = `Refused by ${personName(group)} as ${groupName(group)}`;
      if (reasons) {
        title += ` — ${reasons}`;
      }
      if (comment) {
        title += `. ${comment}`;
      }
      return `${title}${extra}`;
    }
    default:
      return null;
  }
};

const logEntryTitle = (entry: GuardrailDecisionLogEntry): string => {
  switch (entry.decision) {
    case "APPROVED":
      return `Approved by ${logPersonName(entry)} as ${logGroupName(entry)}`;
    case "REFUSED": {
      const reasons = (entry.reasons ?? [])
        .map((reason) => text(reason.label))
        .filter((label) => label !== "")
        .join(", ");
      const comment = text(entry.comment);
      let title = `Refused by ${logPersonName(entry)} as ${logGroupName(entry)}`;
      if (reasons) {
        title += ` — ${reasons}`;
      }
      if (comment) {
        title += `. ${comment}`;
      }
      return title;
    }
    default: {
      const unexpected: never = entry.decision;
      return unexpected;
    }
  }
};

const logEntryIcon = (entry: GuardrailDecisionLogEntry): HistoryIcon =>
  entry.decision === "REFUSED" ? "xmark" : "check";

const sortLogEntries = (
  log: GuardrailDecisionLogEntry[]
): GuardrailDecisionLogEntry[] =>
  [...log].sort((left, right) => {
    const leftAt = text(left.actedAt);
    const rightAt = text(right.actedAt);
    if (leftAt !== "" && rightAt !== "") {
      const cmp = leftAt.localeCompare(rightAt);
      if (cmp !== 0) {
        return cmp;
      }
    } else if (leftAt !== "") {
      return -1;
    } else if (rightAt !== "") {
      return 1;
    }
    return left.order - right.order;
  });

const historyRowFromLogEntry = (
  entry: GuardrailDecisionLogEntry,
  index: number
): HistoryRow => ({
  id: `log-${index}-${entry.order}-${entry.approverGroupId}`,
  icon: logEntryIcon(entry),
  title: logEntryTitle(entry),
  at: text(entry.actedAt) || null,
  atKind: "datetime",
  actorPersonId: text(entry.personId) || null,
  actorPersonName: logPersonName(entry),
  rescindedBy: entry.rescindedBy ?? null,
});

const stampActedRows = (steps: ApprovalStep[]): HistoryRow[] => {
  const rows: HistoryRow[] = [];
  for (const step of steps) {
    if (step.kind !== "group") {
      continue;
    }
    for (const group of step.groups ?? []) {
      const title = actedTitle(group);
      if (title) {
        rows.push({
          id: `acted-${step.order}-${group.approverGroupId}`,
          icon: group.status === "REFUSED" ? "xmark" : "check",
          title,
          at: text(group.actedAt) || null,
          atKind: "datetime",
          actorPersonId: text(group.personId) || null,
          actorPersonName: personName(group),
        });
      }
    }
  }
  return rows;
};

const waitingTitle = (group: ApprovalGroup): string => {
  let title = `Waiting for ${groupName(group)}`;
  if (typeof group.slaHours === "number" && Number.isFinite(group.slaHours)) {
    title += ` (SLA ${group.slaHours} h)`;
  }
  if (group.pastSla === true) {
    title += " · Past SLA";
  }
  return title;
};

/**
 * Oldest first, in chain order. A reason row has no date until the API
 * exposes actedAt on the reason step.
 * Decision rows come from guardrailDecisionLog when present; otherwise the
 * current step stamp (pre-slice decisions with no log row yet).
 */
export const guardrailHistory = (
  requisition: HistoryRequisition
): HistoryRow[] => {
  const rows: HistoryRow[] = [
    {
      id: "created",
      icon: "file",
      title: "Request created",
      at: text(requisition.openedDate) || null,
      atKind: "date",
    },
  ];
  const evaluation = requisition.guardrailEvaluation;
  if (!evaluation) {
    return rows;
  }
  const steps = ordered(evaluation.steps);
  const evaluatedAt = text(evaluation.evaluatedAt) || null;
  if (evaluatedAt) {
    rows.push({
      id: "evaluated",
      icon: "clipboard-check",
      title: evaluatedTitle(evaluation),
      at: evaluatedAt,
      atKind: "datetime",
    });
  }
  const routed = routedTitle(evaluation, steps);
  if (routed) {
    rows.push({
      id: "routed",
      icon: "paper-plane",
      title: routed,
      at: evaluatedAt,
      atKind: "datetime",
    });
  }
  const waiting: HistoryRow[] = [];
  for (const step of steps) {
    if (step.kind === "reason" && text(step.reason) !== "") {
      rows.push({
        id: `reason-${step.order}`,
        icon: "comment",
        title: "Exception reason saved",
        at: text(step.actedAt) || null,
        atKind: "datetime",
      });
    }
    if (step.kind !== "group") {
      continue;
    }
    for (const group of step.groups ?? []) {
      if (group.status === "PENDING" && text(group.clockStartedAt) !== "") {
        waiting.push({
          id: `waiting-${step.order}-${group.approverGroupId}`,
          icon: "clock",
          title: waitingTitle(group),
          at: text(group.clockStartedAt),
          atKind: "datetime",
          datePrefix: "since",
        });
      }
    }
  }
  const log = requisition.guardrailDecisionLog ?? [];
  const decisionRows =
    log.length > 0
      ? sortLogEntries(log).map(historyRowFromLogEntry)
      : stampActedRows(steps);
  return [...rows, ...decisionRows, ...waiting];
};
